import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useVoiceStore } from "@/store/useVoiceStore";
import { useDraggable } from "@/hooks/useDraggable";
import { getSafeAreaBottom } from "@/utils/safeArea";
import { unlockSharedAudio, playTtsViaWebAudio, installPageTouchListener } from "@/utils/audioPlayer";

const BRAND = "#FF6B35";
const BRAND_DARK = "#E55A29";

/**
 * ★ 浮层专用 AI 语音播报按钮（橙色主题）
 * - 首次渲染时若 msg.url 新增 → 自动播放（Web Audio，无需用户手势）
 * - 也可手动点击播放/停止
 */
function FloatTtsButton({ audioUrl, autoPlay = false }: { audioUrl: string; autoPlay?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const playingRef = useRef(false);
  const loadingRef = useRef(false);

  // 停止当前播放并重置状态
  const stopPlayback = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    playingRef.current = false;
    setPlaying(false);
    setLoading(false);
    useVoiceStore.getState().setIsTtsPlaying(false);
  }, []);

  const play = useCallback((url: string) => {
    stopPlayback();
    loadingRef.current = true;
    setLoading(true);
    setError(false);
    cleanupRef.current = playTtsViaWebAudio(url, {
      onCanPlay: () => { loadingRef.current = false; setLoading(false); },
      onPlay: () => { playingRef.current = true; setPlaying(true); useVoiceStore.getState().setIsTtsPlaying(true); },
      onEnded: () => { playingRef.current = false; setPlaying(false); useVoiceStore.getState().setIsTtsPlaying(false); },
      onPause: () => { playingRef.current = false; setPlaying(false); useVoiceStore.getState().setIsTtsPlaying(false); },
      onError: () => { loadingRef.current = false; setLoading(false); setError(true); playingRef.current = false; setPlaying(false); useVoiceStore.getState().setIsTtsPlaying(false); },
    });
  }, [stopPlayback]);

  // ★ 自动播放：仅在 autoPlay 时（msg.url 为新增）触发一次
  useEffect(() => {
    if (autoPlay && audioUrl) {
      console.log("[FloatTts] 自动播报 AI 回复");
      play(audioUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 卸载时清理
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      useVoiceStore.getState().setIsTtsPlaying(false);
    };
  }, []);

  const handleToggle = useCallback(() => {
    if (loadingRef.current) return; // 加载中防止重复点击（与 ai-voice TtsPlayButton 一致）
    unlockSharedAudio();
    if (playingRef.current) {
      stopPlayback();
    } else {
      play(audioUrl);
    }
  }, [audioUrl, play, stopPlayback]);

  return (
    <button
      onClick={handleToggle}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer active:scale-95 transition-all"
      style={{
        background: playing ? "rgba(255,107,53,0.18)" : "rgba(255,255,255,0.06)",
        border: `1px solid ${playing ? BRAND : "rgba(255,255,255,0.15)"}`,
      }}
    >
      {loading ? (
        <span className="inline-block w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "#ccc", borderTopColor: "#999" }} />
      ) : error ? (
        <i className="ri-error-warning-line" style={{ color: "#EF4444", fontSize: 13 }} />
      ) : playing ? (
        <span className="flex items-end gap-[1.5px]" style={{ height: 14 }}>
          {[0, 0.12, 0.24, 0.12, 0].map((d, i) => (
            <span key={i} className="inline-block w-[2px] rounded-full"
              style={{ background: BRAND, animation: `wvf 0.55s ease-in-out ${d}s infinite`, transformOrigin: "center", height: "100%" }} />
          ))}
        </span>
      ) : (
        <i className="ri-volume-up-line" style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }} />
      )}
      <span className="text-[11px] font-medium"
        style={{ color: playing ? BRAND : "rgba(255,255,255,0.7)" }}>
        {loading ? "加载中" : error ? "播放失败" : playing ? "正在播报" : "播报"}
      </span>
    </button>
  );
}

/**
 * ★ 浮层专用气泡组件（橙色主题，完全独立于 ai-voice 页面的 ChatBubble）
 * 仅用于 AiFloatButton 的语音浮窗，不改动 ai-voice 整页任何样式。
 */
function FloatBubble({
  msg,
  onNav,
}: {
  msg: {
    role: "user" | "ai";
    text: string;
    time: string;
    isTyping?: boolean;
    isVoice?: boolean;
    url?: string;
    action?: { path: string; label: string; icon: string };
  };
  onNav: (path: string) => void;
}) {
  const isUser = msg.role === "user";

  // ★ 检测 TTS url 是否为本次新增（新增 → 自动播报）
  const prevUrlRef = useRef<string | undefined>(msg.url);
  const ttsAutoPlay = !!(msg.url && !prevUrlRef.current);
  useEffect(() => {
    prevUrlRef.current = msg.url;
  });

  // 用户语音气泡：橙色渐变 + 播放波形
  const userBubble = (() => {
    if (!msg.isVoice) return null;
    const bars = [0.4, 0.7, 1, 0.6, 0.85, 0.5, 0.9, 0.65, 1, 0.45, 0.75, 0.55];
    return (
      <div
        className="flex flex-col items-end gap-1.5"
      >
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-[18px_4px_18px_18px]"
          style={{
            background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})`,
            boxShadow: "0 2px 8px rgba(255,107,53,0.25)",
            minWidth: 140,
            maxWidth: 200,
          }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.25)" }}
          >
            <i className="ri-mic-fill" style={{ color: "#fff", fontSize: 15 }} />
          </div>
          <div className="flex items-center gap-[2px] flex-1" style={{ height: 26 }}>
            {bars.map((v, i) => (
              <div
                key={i}
                className="rounded-full flex-shrink-0"
                style={{
                  width: 2.5,
                  height: v * 26,
                  background: "rgba(255,255,255,0.8)",
                }}
              />
            ))}
          </div>
        </div>
        {msg.text && (
          <div
            className="px-3 py-1.5 rounded-xl max-w-[220px]"
            style={{
              background: "rgba(255,107,53,0.08)",
              border: "1px solid rgba(255,107,53,0.25)",
            }}
          >
            <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.9)" }}>
              {msg.text}
            </p>
          </div>
        )}
      </div>
    );
  })();

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
      {!isUser && (
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` }}
        >
          <i className="ri-sparkling-2-fill text-white" style={{ fontSize: 15 }} />
        </div>
      )}
      <div className={`flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`} style={{ maxWidth: "82%" }}>
        {userBubble ?? (
          <div
            className="px-4 py-3 text-sm leading-relaxed"
            style={{
              background: isUser
                ? `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})`
                : "rgba(255,107,53,0.08)",
              color: isUser ? "#fff" : "rgba(255,255,255,0.95)",
              borderRadius: isUser ? "18px 4px 18px 18px" : "4px 18px 18px 18px",
              whiteSpace: "pre-line",
              border: isUser ? "none" : "1px solid rgba(255,107,53,0.25)",
              lineHeight: "1.65",
              boxShadow: isUser ? "0 2px 8px rgba(255,107,53,0.25)" : "none",
            }}
          >
            {msg.isTyping ? (
              <div className="flex gap-1.5 items-center py-0.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-1.5 h-1.5 rounded-full"
                    style={{
                      background: BRAND,
                      animation: `bDot 1s ease-in-out ${i * 0.2}s infinite`,
                    }}
                  />
                ))}
              </div>
            ) : (
              <>{msg.text}</>
            )}
          </div>
        )}

        {/* 跳转按钮（AI 消息） */}
        {!isUser && !msg.isTyping && msg.action && (
          <button
            onClick={() => onNav(msg.action!.path)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-semibold cursor-pointer active:scale-95 transition-all whitespace-nowrap mt-0.5"
            style={{
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})`,
              color: "#fff",
              letterSpacing: "0.01em",
            }}
          >
            <i className={msg.action.icon} style={{ fontSize: 13 }} />
            {msg.action.label}
            <i className="ri-arrow-right-line" style={{ fontSize: 11, opacity: 0.7 }} />
          </button>
        )}

        {/* AI 语音播报按钮（有 TTS url 时显示，新增 url 自动播放） */}
        {!isUser && !msg.isTyping && !msg.isVoice && msg.url && (
          <div className="mt-0.5">
            <FloatTtsButton audioUrl={msg.url} autoPlay={ttsAutoPlay} />
          </div>
        )}

        <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.35)" }}>
          {msg.time}
        </span>
      </div>
    </div>
  );
}

// 不显示AI悬浮按钮的页面
const EXCLUDED_PATHS = ["/ai-voice", "/"];

// ── 页面上下文感知 ────────────────────────────────────────
function getPageContext(pathname: string): string {
  if (pathname.startsWith("/hotel-detail")) return "hotel_detail";
  if (pathname.startsWith("/hotels")) return "hotel_list";
  if (pathname.startsWith("/car-detail")) return "car_detail";
  if (pathname.startsWith("/cars")) return "car_list";
  if (pathname.startsWith("/route-detail")) return "route_detail";
  if (pathname.startsWith("/routes")) return "route_list";
  if (pathname.startsWith("/guide-detail")) return "guide_detail";
  if (pathname.startsWith("/guides")) return "guide_list";
  if (pathname.startsWith("/order-form")) return "order_form";
  if (pathname.startsWith("/payment")) return "payment";
  if (pathname.startsWith("/orders")) return "orders";
  if (pathname.startsWith("/inquiry-orders")) return "inquiry";
  if (pathname.startsWith("/profile")) return "profile";
  if (pathname.startsWith("/wallet")) return "wallet";
  if (pathname.startsWith("/contracts")) return "contracts";
  if (pathname.startsWith("/insurance")) return "insurance";
  if (pathname.startsWith("/ai-plan")) return "ai_plan";
  return "general";
}

// ── 意图分析（带页面上下文） ──────────────────────────────
type ActionType =
  | { kind: "navigate"; path: string; label: string }
  | {
      kind: "page_action";
      event: string;
      payload?: Record<string, unknown>;
      label: string;
    }
  | { kind: "answer" };

interface AIResult {
  text: string;
  action?: ActionType;
  recognized?: string;
  /** 查询类型消息：train / car / food / guide / flight 等 */
  msg_type?: string;
  /** 查询类型消息的结构化数据（如火车票列表、美食推荐等） */
  queryData?: any;
}

function analyzeWithContext(text: string, pageCtx: string): AIResult {
  void text;
  void pageCtx;
  return { text: "1" };
}

// ── 语音波形 ──────────────────────────────────────────────
function VoiceWave({
  active,
  size = "md",
}: {
  active: boolean;
  size?: "sm" | "md";
}) {
  const bars =
    size === "sm"
      ? [0.4, 0.7, 1, 0.8, 0.5, 0.9, 0.6, 0.4]
      : [0.3, 0.6, 0.9, 1, 0.7, 0.85, 0.5, 1, 0.65, 0.4, 0.3, 0.7];
  const h = size === "sm" ? 20 : 28;
  return (
    <div
      className="flex items-center justify-center gap-[2px]"
      style={{ height: h }}
    >
      {bars.map((v, i) => (
        <div
          key={i}
          className="rounded-full"
          style={{
            width: size === "sm" ? 2 : 2.5,
            height: active ? v * h : 3,
            background: active
              ? "rgba(255,255,255,0.9)"
              : "rgba(255,255,255,0.35)",
            transition: "height 0.08s ease",
            animation: active
              ? `wvBar 0.6s ease-in-out ${i * 0.06}s infinite alternate`
              : "none",
          }}
        />
      ))}
    </div>
  );
}

// ── 主组件 ────────────────────────────────────────────────
export default function AiFloatButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    vadState,
    isListening,
    isPressRecording,
    deviceStatus,
    stopListening,
    startPressToRecord,
    stopPressToRecord,
    conversationMessages,
    isAudioProcessing,
    micError,
    setMicError,
  } = useVoiceStore();

  const [visible, setVisible] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [result, setResult] = useState<AIResult | null>(null);
  const [processing, setProcessing] = useState(false);
  const [actionDone, setActionDone] = useState(false);

  // ★ 全局安装音频解锁监听（浮层在非 ai-voice 页面，需保证 TTS 自动播放不被浏览器拦截）
  useEffect(() => {
    installPageTouchListener();
  }, []);

  // ★ 按住录音状态（微信式交互）
  const [isHolding, setIsHolding] = useState(false);
  const holdStartRef = useRef<number>(0);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ★ 记录本次语音发送后新增的 AI 消息 ID，用于在浮层中展示 AI 回复
  const lastHandledAiIdRef = useRef<number | null>(null);
  // ★ 标记用户是否刚在浮层中发过语音（用于区分历史消息与本次新回复）
  const sentMsgRef = useRef(false);
  // ★ 对话列表滚动到底部 ref（有消息新增时自动滚底）
  const floatChatEndRef = useRef<HTMLDivElement | null>(null);
  const floatChatContainerRef = useRef<HTMLDivElement | null>(null);

  /**
   * DEBUG 日志：仅打印到浏览器 console，不再写入浮层面板。
   * （浮窗内已移除调试面板，pushDbg 仅保留 console.log 便于真机远程调试）
   */
  const pushDbg = useCallback((tag: string, ...args: unknown[]) => {
    console.log(`[${tag}] ${args.map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a))).join(" ")}`);
  }, []);

  const pageCtx = getPageContext(location.pathname);
  const pageCtxRef = useRef(pageCtx);

  useEffect(() => {
    pageCtxRef.current = pageCtx;
  }, [pageCtx]);

  // ── ★ 订阅 AI 回复：在浮层中显示 SSE 返回的答案 ──
  //   navigation 消息由 App.tsx 全局处理自动跳转（会触发路由变化并关闭浮层）
  //   普通文本回复追加到 conversationMessages，这里取最新一条 AI 消息展示
  //   与 ai-voice 页面保持一致：展示用户转写文字 + AI 回复文本（含查询卡片数据）
  useEffect(() => {
    if (!voiceOpen) return;
    if (!sentMsgRef.current) return;
    if (conversationMessages.length === 0) return;

    // 找到最后一条 AI 非 typing 消息（SSE 已追加的完整回复）
    let latestAiId: number | null = null;
    let latestText = "";
    let latestMsgType: string | undefined;
    let latestQueryData: any;
    for (let i = conversationMessages.length - 1; i >= 0; i--) {
      const m = conversationMessages[i];
      if (m.role === "ai" && !m.isTyping && m.text) {
        latestAiId = m.id;
        latestText = m.text;
        latestMsgType = m.msg_type;
        latestQueryData = m.queryData;
        break;
      }
    }

    if (latestAiId === null || latestAiId === lastHandledAiIdRef.current) return;

    pushDbg("sse", "收到AI回复 id=", latestAiId, "text=", latestText.substring(0, 20));
    // 找本次语音发送对应的用户消息（优先取最后一条非 🎤 占位的用户语音/文本）
    let recognizedText = "";
    for (let i = conversationMessages.length - 1; i >= 0; i--) {
      const m = conversationMessages[i];
      if (m.role === "user" && m.text && m.text !== "\u{1F3A4}") {
        recognizedText = m.text;
        break;
      }
    }

    lastHandledAiIdRef.current = latestAiId;
    setProcessing(false);
    setResult({
      text: latestText,
      recognized: recognizedText || undefined,
      action: undefined,
      msg_type: latestMsgType,
      queryData: latestQueryData,
    });
  }, [conversationMessages, voiceOpen]);

  useEffect(() => {
    const path = location.pathname;
    const isExcluded =
      EXCLUDED_PATHS.includes(path) ||
      path.startsWith("/admin") ||
      path.startsWith("/supplier");
    setVisible(!isExcluded);
    // 切页时收起面板
    setVoiceOpen(false);
    setResult(null);
    setActionDone(false);
    setProcessing(false);
    sentMsgRef.current = false;
    lastHandledAiIdRef.current = null;
  }, [location.pathname]);

  const executeAction = useCallback(
    (action: ActionType) => {
      setActionDone(true);
      if (action.kind === "navigate") {
        setTimeout(() => {
          setVoiceOpen(false);
          navigate(action.path);
        }, 400);
      } else if (action.kind === "page_action") {
        window.dispatchEvent(
          new CustomEvent(action.event, { detail: action.payload ?? {} }),
        );
        setTimeout(() => setVoiceOpen(false), 800);
      }
    },
    [navigate],
  );

  // ★ 对话列表中 FloatBubble 的跳转回调（点击跳转按钮时关闭浮层并导航）
  const handleFloatNav = useCallback(
    (path: string) => {
      pushDbg("nav", "浮层跳转 →", path);
      setVoiceOpen(false);
      navigate(path);
    },
    [navigate, pushDbg],
  );

  // ★ 对话列表新增消息时自动滚到底部
  useEffect(() => {
    if (!voiceOpen) return;
    const el = floatChatEndRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [voiceOpen, conversationMessages]);

  // 非 AI 对话页的语音识别结果处理（当前为占位，后续可扩展）

  // ★ 常驻对讲状态已改为按住说话模式，不再自动监听（原逻辑已禁用）
  // 用户需要按住麦克风按钮才能开始录音，松手后停止并发送
  useEffect(() => {
    if (!visible) return;
    // 停止任何正在运行的自动监听（向后兼容）
    if (isListening && !isPressRecording) {
      stopListening();
    }
  }, [visible, isListening, isPressRecording, stopListening]);

  const isRec = isPressRecording || isHolding;
  // ★ 已移除 listening 状态显示（改为按住说话后不再有常驻对讲）
  const btnState: "idle" | "open" | "recording" = isRec
    ? "recording"
    : voiceOpen
      ? "open"
      : "idle";

  const { containerStyle, onPointerDown, isDragging } = useDraggable({
    initialRight: 16,
    initialBottom: 90 + getSafeAreaBottom(),
    size: 58,
  });

  // ── ★ 按住录音（微信式交互，与 AiVoicePage 一致）──

  /** 按下/触摸开始 */
  const handlePressStart = useCallback(async () => {
    pushDbg("voice", "开始按住说话 (deviceStatus=", deviceStatus, ", isAudioProcessing=", isAudioProcessing, ")");
    if (deviceStatus !== "available" || isAudioProcessing) {
      pushDbg("touch", "pressSTART被拦截 (deviceStatus=", deviceStatus, ")");
      return;
    }
    // 开始新一轮录音时，清除上次结果并进入处理中占位
    setResult(null);
    setActionDone(false);
    setProcessing(false);
    setMicError(null);
    setIsHolding(true);
    holdStartRef.current = Date.now();
    pressEndLockRef.current = false; // 新一轮开始，重置锁
    // ★ 会话序号自增：用于隔离快速连续按住时旧会话的异步收尾，避免覆盖新会话状态
    pressSessionRef.current += 1;
    currentPressSessionRef.current = pressSessionRef.current;
    pushDbg("voice", "已按下, 150ms后开始录音");

    // 短暂延迟后开始录音，防止误触
    holdTimerRef.current = setTimeout(async () => {
      const ok = await startPressToRecord();
      pushDbg("voice", ok ? "✓ 开始录音" : "✗ 录音启动失败", "vadState=", vadState, "isPressRecording=", isPressRecording);
      if (!ok) {
        setIsHolding(false);
        // ★ 录音启动失败（如麦克风授权被拒）也展开抽屉，以便展示 micError 错误提示
        setVoiceOpen(true);
        return;
      }
      // ★ 确认开始录音后才展开抽屉（避免拖动悬浮球时抽屉一闪而过）
      setVoiceOpen(true);
    }, 150);
  }, [deviceStatus, startPressToRecord, isAudioProcessing, isPressRecording, vadState, pushDbg]);

  // 同步锁：防止按钮 pointerup 与全局兜底重复触发
  const pressEndLockRef = useRef(false);
  // ★ 会话序号：记录全局累计会话号与当前活动会话号，隔离快速连按的异步收尾
  const pressSessionRef = useRef(0);
  const currentPressSessionRef = useRef(0);
  // ★ 拖拽实时标记（ref 同步 isDragging，供 handlePressEnd 同步判断，防止拖拽松手被全局兜底误发送）
  const isDraggingRef = useRef(false);
  isDraggingRef.current = isDragging;

  /** 松手/触摸结束 → 发送 */
  const handlePressEnd = useCallback(async (cancelled = false) => {
    pushDbg("touch", "pressEND", "cancelled=", cancelled, "isHolding=", isHolding, "isPressRecording=", isPressRecording);
    // 同步锁：已锁定表示正在处理上一次结束流程，直接忽略后续重复触发
    if (pressEndLockRef.current) {
      pushDbg("touch", "pressEND 重复触发, 忽略");
      return;
    }
    if (!isHolding) {
      pushDbg("touch", "pressEND 被拦截: isHolding=false");
      return;
    }
    pressEndLockRef.current = true;

    // 清除未开始的定时器（按得太快）
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    // 按住时间太短视为取消
    const heldMs = Date.now() - holdStartRef.current;
    if (heldMs < 300 && !cancelled) cancelled = true;
    // ★ 若松手瞬间仍处于拖拽中（全局兜底先于 isDragging effect 触发），强制取消，避免误发送
    if (isDraggingRef.current && !cancelled) cancelled = true;

    setIsHolding(false);
    // ★ 记录本次会话号：若 await 期间用户已开始新一轮按住（会话号变化），旧收尾不再更新 UI，避免状态错乱
    const sessionAtEnd = currentPressSessionRef.current;
    pushDbg("voice", cancelled ? "已松手, 取消本次录音" : "已松手, 结束录音并发送", "cancelled=", cancelled);

    // 停止录音并输出 Blob；返回是否确实进入了录音停止流程（有实际输出/取消动作）
    const didStop = await stopPressToRecord(cancelled);

    // ★ 正常发送（非取消且确实有录音动作）→ 标记已发送，进入"AI 分析中"状态
    // 避免：麦克风授权失败/完全空闲态下 stopPressToRecord 提前返回（didStop=false）时仍误显示"分析中"
    if (!cancelled && didStop && sessionAtEnd === currentPressSessionRef.current) {
      sentMsgRef.current = true;
      setResult(null);
      setActionDone(false);
      setProcessing(true);
      // 重置已处理标记，确保新回复能触发展示
      lastHandledAiIdRef.current = null;
      pushDbg("voice", "✓ 语音已发送, 进入分析中");
    }
  }, [isHolding, stopPressToRecord, isPressRecording, pushDbg]);

  /** 触摸滑动到按钮外 → 取消 */
  const handlePressCancel = useCallback(() => {
    handlePressEnd(true);
  }, [handlePressEnd]);

  // ★ 按住悬浮按钮：同时处理「拖拽」与「开始录音」，并自动展开抽屉
  // 注意：不使用 shouldSkipClick —— 拖拽行为由下方「isDragging → 取消录音+收起抽屉」的
  // effect 保护，若用 shouldSkipClick 会在拖拽结束后第一次按住时被误跳过（开不了抽屉也说不了话）。
  const handleFloatPressDown = useCallback(
    (e: React.PointerEvent) => {
      // 重置展示状态（抽屉在确认开始录音后才展开，见 handlePressStart）
      setResult(null);
      setProcessing(false);
      setActionDone(false);
      sentMsgRef.current = false;
      lastHandledAiIdRef.current = null;
      // 先交给拖拽逻辑（负责指针捕获与位移检测）
      onPointerDown(e);
      // 再开始按住录音（150ms 延迟防误触，拖拽时会自动取消，不会展开抽屉）
      void handlePressStart();
    },
    [onPointerDown, handlePressStart],
  );

  // ── ★ 全局兜底：按住录音期间，window 上任何松手都强制结束（防止按钮 pointerup/touchend 丢失）──
  const isHoldingRef = useRef(isHolding);
  isHoldingRef.current = isHolding;
  const pressEndRef = useRef(handlePressEnd);
  pressEndRef.current = handlePressEnd;

  useEffect(() => {
    const onUp = () => {
      if (isHoldingRef.current) {
        pushDbg("touch", "[全局兜底] window pointerup/touchend 检测到松手, 强制结束录音");
        void pressEndRef.current(false);
      }
    };
    const onCancel = () => {
      if (isHoldingRef.current) {
        pushDbg("touch", "[全局兜底] window pointercancel/touchcancel, 取消录音");
        void pressEndRef.current(true);
      }
    };
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onCancel);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onCancel);
    };
  }, [pushDbg]);

  // ★ 按住悬浮按钮录音时，若触发拖拽（位移超过阈值）则立即取消录音并收起抽屉，避免误发
  useEffect(() => {
    if (isDragging && isHoldingRef.current) {
      pushDbg("touch", "检测到拖拽, 取消按住录音");
      handlePressCancel();
      setVoiceOpen(false);
    }
  }, [isDragging, handlePressCancel, pushDbg]);

  if (!visible) return null;

  return (
    <div style={containerStyle}>
      {/* 语音对讲浮层 */}
      {voiceOpen && (
          <div
            className="pointer-events-auto fixed inset-0 z-50 flex items-end justify-center"
            style={{
              background: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(5px)",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setVoiceOpen(false);
              }
            }}
          >
            <div
              className="w-full max-w-sm rounded-t-3xl overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, #0D0D1A 0%, #1A0A00 60%, #0D0D1A 100%)",
                animation: "slideUp 0.25s ease-out",
                paddingBottom: "env(safe-area-inset-bottom, 16px)",
              }}
            >
              {/* 顶部拖拽条 */}
              <div className="flex justify-center pt-3 pb-1">
                <div
                  className="w-10 h-1 rounded-full"
                  style={{ background: "rgba(255,255,255,0.2)" }}
                />
              </div>

              {/* 标题栏 */}
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center relative"
                    style={{
                      background: "linear-gradient(135deg, #FF6B35, #FF9A5C)",
                    }}
                  >
                    <i
                      className="ri-sparkling-2-fill text-white"
                      style={{ fontSize: 15 }}
                    />
                    {isRec && (
                      <span
                        className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2"
                        style={{
                          background: "#FF2D2D",
                          borderColor: "#0D0D1A",
                          animation: "recBlink 1s ease-in-out infinite",
                        }}
                      />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white">
                        美伊 AI
                      </span>
                      {isRec && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-bold tracking-wide"
                          style={{
                            background: "rgba(255,30,30,0.2)",
                            color: "#FF6B6B",
                            border: "1px solid rgba(255,30,30,0.3)",
                            animation: "recBlink 1s ease-in-out infinite",
                          }}
                        >
                          ● 聆听中
                        </span>
                      )}
                      {!isRec && voiceOpen && !processing && !result && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full"
                          style={{
                            background: "rgba(255,107,53,0.12)",
                            color: "rgba(255,150,100,0.8)",
                            border: "1px solid rgba(255,107,53,0.2)",
                          }}
                        >
                          待命
                        </span>
                      )}
                      {processing && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full"
                          style={{
                            background: "rgba(255,200,50,0.12)",
                            color: "rgba(255,200,80,0.9)",
                            border: "1px solid rgba(255,200,50,0.2)",
                          }}
                        >
                          ◌ 分析中
                        </span>
                      )}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {pageCtx === "hotel_detail" && "酒店详情页 · 可帮你预订"}
                      {pageCtx === "car_detail" && "租车详情页 · 可帮你预订"}
                      {pageCtx === "route_detail" && "线路详情页 · 可帮你预订"}
                      {pageCtx === "payment" && "支付页面 · 可帮你完成支付"}
                      {pageCtx === "orders" && "订单页面 · 可查询订单状态"}
                      {pageCtx === "hotel_list" && "酒店列表 · 可帮你筛选"}
                      {pageCtx === "car_list" && "租车列表 · 可帮你筛选"}
                      {![
                        "hotel_detail",
                        "car_detail",
                        "route_detail",
                        "payment",
                        "orders",
                        "hotel_list",
                        "car_list",
                      ].includes(pageCtx) && "随时待命 · 智能操作"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setVoiceOpen(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.08)" }}
                  >
                    <i
                      className="ri-close-line"
                      style={{ color: "rgba(255,255,255,0.6)", fontSize: 16 }}
                    />
                  </button>
                </div>
              </div>

              {/* 内容区 */}
              <div className="px-5 py-2 min-h-[80px] flex flex-col justify-center">
                {/* 发送/识别错误提示 */}
                {micError && (
                  <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-xl text-xs"
                    style={{
                      background: "rgba(255,70,70,0.12)",
                      border: "1px solid rgba(255,70,70,0.25)",
                      color: "#FF8A8A",
                    }}
                  >
                    <i className="ri-error-warning-line flex-shrink-0" style={{ fontSize: 14 }} />
                    <span>{micError.message}</span>
                  </div>
                )}

                {/* ── 对话消息列表（浮层专用橙色 FloatBubble，用户/AI 气泡 + 跳转按钮）── */}
                {conversationMessages.length > 0 ? (
                  <div
                    ref={floatChatContainerRef}
                    className="max-h-[300px] overflow-y-auto rounded-2xl p-3"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    {conversationMessages.map((m) => (
                      <div key={m.id} className="mb-3 last:mb-0">
                        <FloatBubble
                          msg={m}
                          onNav={handleFloatNav}
                        />
                      </div>
                    ))}
                    <div ref={floatChatEndRef} />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p
                      className="text-sm"
                      style={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      ★ 按住下方麦克风说话
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: "rgba(255,255,255,0.25)" }}
                    >
                      可以说：「帮我预订」「选5月1日」「微信免押」
                    </p>
                  </div>
                )}

                {isRec && (
                  <div className="flex flex-col items-center gap-2">
                    <VoiceWave active={true} />
                    <p
                      className="text-sm font-semibold"
                      style={{ color: BRAND }}
                    >
                      正在录音...
                    </p>
                  </div>
                )}

                {processing && conversationMessages.length === 0 && (
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex gap-1.5">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="w-2 h-2 rounded-full"
                          style={{
                            background: BRAND,
                            animation: `bDot 0.9s ease-in-out ${i * 0.2}s infinite`,
                          }}
                        />
                      ))}
                    </div>
                    <p
                      className="text-xs"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      AI 正在分析...
                    </p>
                  </div>
                )}
              </div>

              {/* 麦克风按钮 - ★ 改为按住说话模式（微信式交互） */}
              <div className="flex items-center justify-center py-6">
                <div
                  className="relative flex items-center justify-center"
                  style={{ width: 80, height: 80 }}
                >
                  {isRec && (
                    <>
                      <div
                        className="absolute rounded-full"
                        style={{
                          width: 80,
                          height: 80,
                          border: "2px solid rgba(255,45,45,0.4)",
                          animation: "micRing 1.2s ease-out infinite",
                        }}
                      />
                      <div
                        className="absolute rounded-full"
                        style={{
                          width: 66,
                          height: 66,
                          border: "2px solid rgba(255,45,45,0.5)",
                          animation: "micRing 1.2s ease-out 0.15s infinite",
                        }}
                      />
                    </>
                  )}
                  <button
                    onPointerDown={handlePressStart}
                    onPointerUp={() => handlePressEnd(false)}
                    onPointerCancel={handlePressCancel}
                    onPointerLeave={handlePressCancel}
                    onContextMenu={(e) => e.preventDefault()}
                    disabled={deviceStatus !== "available"}
                    className="relative flex items-center justify-center rounded-full cursor-pointer select-none active:scale-95 transition-all touch-none"
                    style={{
                      width: 60,
                      height: 60,
                      background: isRec
                        ? "linear-gradient(145deg, #FF2D2D, #FF6060)"
                        : "linear-gradient(145deg, #FF6B35, #FF9A5C)",
                      boxShadow: isRec
                        ? "0 0 30px rgba(255,45,45,0.65)"
                        : "0 6px 24px rgba(255,107,53,0.5)",
                      transform: isRec ? "scale(1.08)" : "scale(1)",
                      opacity: deviceStatus !== "available" ? 0.5 : 1,
                    }}
                  >
                    {isRec ? (
                      <VoiceWave active={true} size="sm" />
                    ) : (
                      <i
                        className="ri-mic-2-line text-white"
                        style={{ fontSize: 26 }}
                      />
                    )}
                  </button>
                </div>
              </div>

              <p
                className="text-center text-xs pb-4"
                style={{ color: "rgba(255,255,255,0.18)" }}
              >
                {isRec
                  ? "松开发送"
                  : deviceStatus === "available"
                    ? "按住说话"
                    : "麦克风不可用"}
              </p>
            </div>
          </div>
        )}

        {/* ── 常驻对讲状态横幅已禁用（已改为按住说话模式，不再自动监听） ── */}

        {/* ── 悬浮主按钮（四态设计）── */}
        <div
          className="pointer-events-auto absolute"
          onPointerDown={onPointerDown}
          style={{
            bottom: 0,
            right: 0,
          }}
        >
          {/* 录音状态：聆听中浮标 */}
          {btnState === "recording" && (
            <div
              className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap"
              style={{
                background: "rgba(255,30,30,0.92)",
                backdropFilter: "blur(8px)",
                boxShadow: "0 4px 16px rgba(255,30,30,0.5)",
                animation: "recBlink 1s ease-in-out infinite",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-white"
                style={{ animation: "recBlink 0.8s ease-in-out infinite" }}
              />
              <span className="text-white text-xs font-bold tracking-wide">
                聆听中
              </span>
            </div>
          )}

          {/* 面板开启但未录音：待命浮标 */}
          {btnState === "open" && (
            <div
              className="absolute -top-9 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2.5 py-1 rounded-full whitespace-nowrap"
              style={{
                background: "rgba(30,30,30,0.85)",
                backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,107,53,0.3)",
              }}
            >
              <span
                className="text-xs font-medium"
                style={{ color: "rgba(255,150,80,0.9)" }}
              >
                关闭
              </span>
            </div>
          )}

          <button
            onPointerDown={handleFloatPressDown}
            onPointerUp={() => handlePressEnd(false)}
            onPointerCancel={handlePressCancel}
            onContextMenu={(e) => e.preventDefault()}
            className="relative flex items-center justify-center rounded-full cursor-pointer select-none active:scale-90 touch-none"
            style={{
              width: btnState === "recording" ? 58 : 52,
              height: btnState === "recording" ? 58 : 52,
              background:
                btnState === "recording"
                  ? "linear-gradient(145deg, #FF2D2D, #FF6060)"
                  : btnState === "open"
                    ? "linear-gradient(145deg, #1A1A2E, #2D1A00)"
                    : "linear-gradient(145deg, #FF6B35, #FF9A5C)",
              boxShadow:
                btnState === "recording"
                  ? "0 0 0 3px rgba(255,45,45,0.25), 0 6px 24px rgba(255,45,45,0.55)"
                  : btnState === "open"
                    ? `0 0 0 2px ${BRAND}, 0 4px 20px rgba(255,107,53,0.35)`
                    : "0 4px 20px rgba(255,107,53,0.45)",
              transition: isDragging ? "none" : "all 0.25s cubic-bezier(0.34,1.56,0.64,1)",
            }}
          >
            {/* ★ 空闲态无动画（已改为按住说话，不需要脉冲提示） */}

            {/* 录音态 pulse rings（红色） */}
            {btnState === "recording" && (
              <>
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "rgba(255,45,45,0.35)",
                    animation: "aiPulse 1.2s ease-out infinite",
                  }}
                />
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "rgba(255,45,45,0.2)",
                    animation: "aiPulse 1.2s ease-out 0.35s infinite",
                  }}
                />
              </>
            )}

            {/* 面板开启态 扫描环 */}
            {btnState === "open" && (
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  border: `2px solid ${BRAND}`,
                  animation: "spinRing 3s linear infinite",
                  opacity: 0.6,
                }}
              />
            )}

            {/* 图标区域 */}
            <div
              className="relative z-10 flex items-center justify-center"
              style={{ width: 28, height: 28 }}
            >
              {btnState === "idle" && (
                <i
                  className="ri-mic-line text-white"
                  style={{ fontSize: 22 }}
                />
              )}
              {btnState === "open" && (
                <i
                  className="ri-mic-line"
                  style={{ fontSize: 20, color: BRAND }}
                />
              )}
              {btnState === "recording" && (
                <div
                  className="flex items-end gap-[2px]"
                  style={{ height: 18 }}
                >
                  {[0.5, 0.8, 1, 0.7, 0.9, 0.6, 0.8].map((v, i) => (
                    <div
                      key={i}
                      className="rounded-full bg-white"
                      style={{
                        width: 2.5,
                        height: v * 18,
                        animation: `wvBar 0.5s ease-in-out ${i * 0.07}s infinite alternate`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </button>
        </div>

        <style>{`
        @keyframes aiPulse {
          0% { transform: scale(1); opacity: 0.6; }
          70% { transform: scale(1.65); opacity: 0; }
          100% { transform: scale(1.65); opacity: 0; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes micRing {
          0% { transform: scale(0.9); opacity: 0.5; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes bDot {
          0%,80%,100% { transform: translateY(0); opacity: 0.3; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes wvBar {
          from { transform: scaleY(0.2); }
          to { transform: scaleY(1); }
        }
        @keyframes wvf {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
        @keyframes recBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spinRing {
          0% { transform: rotate(0deg); border-radius: 50%; clip-path: inset(0 50% 0 0); }
          50% { transform: rotate(180deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
